/**
 * Copyright Mántum All Rights Reserved.
 */
import Container from '../../tab';
import Kanela from '../../../../kanela/body';
import Row from './row';
import getTranslate from '../../../../locales';
import { sort, validator } from '@mantum/kanela';
import { useCallback, useState } from 'react';

import type {
    Detail,
    Error,
    Exclude,
    HttpError,
    Locale,
    Mode,
    Tabs
} from '@mantum/kanela';

export type PropTypes = {
    activeTab?: number;
    columns?: number;
    env?: Record<string, unknown>;
    locale?: Locale;
    mode?: Mode;
    navegable?: boolean;
    onChange?: (name?: string, value?: unknown) => void;
    onClear?: (exclude: Exclude) => void;
    onClick?: (name?: string, value?: unknown) => void;
    onInit?: (ref: Detail<unknown>) => void;
    onMessage?: (
        title: string,
        errors?: HttpError | string[] | Error,
        success?: boolean
    ) => void;
    onUnmount?: (name?: string) => void;
    onListener?: (values?: Record<string, unknown>) => void;
    readonly?: boolean;
    tabs?: Tabs;
    values?: Record<string, unknown>;
};

export default function Tab(props: PropTypes) {
    // prettier-ignore
    const {
        activeTab = 0,
        locale = 'en',
        navegable = false
    } = props;

    const getMessage = useCallback(
        (message: string) => {
            return getTranslate(message, locale);
        },
        [locale]
    );

    const [tabs, setTabs] = useState<Tabs>(() => {
        let active = false;

        const results: Tabs = [];
        props.tabs?.forEach((it) => {
            if (validator.isVisible(it.visible)) {
                results.push(it);
                if (it.active) {
                    active = true;
                }
            }
        });

        if (!active) {
            const index = results.length >= activeTab ? activeTab : 0;
            results[index].active = true;
        }

        return results?.sort(sort.asc);
    });

    const onSelected = (index: number) =>
        setTabs((previous) => {
            return previous.map((it, i) => {
                it.active = false;
                if (index === i) {
                    it.active = true;
                }
                return it;
            });
        });

    const onNavegation = (event: React.MouseEvent<HTMLButtonElement>) => {
        const target = event.target as HTMLButtonElement;
        const index = Number(target.dataset.index);
        if (!isNaN(index)) {
            onSelected(index);
        }
    };

    const index = tabs.findIndex((it) => it.active);

    const previous = index > 0 ? index - 1 : index;
    const next = index + 1 < tabs.length ? index + 1 : index;

    return (
        <>
            <Container>
                {tabs.map((tab, index) => (
                    <Row
                        active={tab.active}
                        index={index}
                        key={tab.title}
                        onSelected={onSelected}
                        testid={tab.testid}
                        title={tab.title}
                    />
                ))}
            </Container>
            {tabs
                .filter((it) => {
                    if (!it.lazy) {
                        return true;
                    }
                    return it.lazy && it.active;
                })
                .map((tab, index) => (
                    <div
                        key={index}
                        className={`mt-4 ${tab.active ? '' : 'hidden'}`}
                    >
                        <Kanela
                            columns={props.columns}
                            env={props.env}
                            mode={props.mode}
                            onChange={props.onChange}
                            onClear={props.onClear}
                            onClick={props.onClick}
                            onInit={props.onInit}
                            onMessage={props.onMessage}
                            onListener={props.onListener}
                            onUnmount={props.onUnmount}
                            readonly={props.readonly}
                            sections={tab.sections}
                            values={props.values}
                        />
                    </div>
                ))}

            {navegable && (
                <div className='flex justify-between w-full pb-8 border-t pt-4'>
                    <div>
                        <div>{getMessage('Previous')}</div>
                        {index > 0 && (
                            <button
                                className='underline cursor-pointer text-primary hover:text-primary-dark'
                                data-index={previous}
                                onClick={onNavegation}
                                type='button'
                            >
                                {tabs[previous].title}
                            </button>
                        )}
                    </div>
                    <div>
                        <div>{getMessage('Next')}</div>
                        {index + 1 < tabs.length && (
                            <button
                                className='underline cursor-pointer text-primary hover:text-primary-dark'
                                data-index={next}
                                onClick={onNavegation}
                                type='button'
                            >
                                {tabs[next].title}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
